/**
 * Global fetch interceptor that:
 * 1. Automatically adds the Company JWT (from jwtHolder) as Authorization
 *    for any fetch() that doesn't already carry one
 * 2. Dispatches 401 events for AuthContext to handle token refresh
 */

import { getJwt } from './jwtHolder';

const originalFetch = window.fetch;

// Debounce 401 event dispatching: coalesce multiple simultaneous 401s into one event.
// When several API calls fail at once (e.g., access token expired), we only need
// to trigger one refresh attempt, not one per failed request.
let pending401Timer: ReturnType<typeof setTimeout> | null = null;
let pending401Callbacks: Array<() => void> = [];

function dispatch401Debounced(retryCallback?: () => void) {
  if (retryCallback) pending401Callbacks.push(retryCallback);

  if (pending401Timer) return; // already scheduled

  pending401Timer = setTimeout(() => {
    const callbacks = [...pending401Callbacks];
    pending401Callbacks = [];
    pending401Timer = null;

    window.dispatchEvent(new CustomEvent('auth:401', {
      detail: {
        retryCallback: callbacks.length > 0
          ? () => callbacks.forEach(cb => cb())
          : undefined,
      },
    }));
  }, 50); // 50ms window to coalesce simultaneous 401s
}

export function setupFetchInterceptor() {
  window.fetch = async (...args) => {
    const [input, init] = args;

    // Step 1: Add Authorization header from the in-memory JWT holder if available.
    // (The legacy localStorage 'token' is no longer written after the Company auth merge.)
    const token = getJwt();
    let modifiedInit = init;

    if (token) {
      const headers = new Headers(init?.headers || {});

      // Only add Authorization header if not already present
      if (!headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      modifiedInit = {
        ...init,
        headers,
      };
    }

    // Step 2: Make the request with auth headers
    const response = await originalFetch(input, modifiedInit);

    // Step 3: Dispatch debounced 401 event for AuthContext to handle
    if (response.status === 401) {
      const url = typeof input === 'string'
        ? input
        : input instanceof Request
          ? input.url
          : input.toString();

      // Don't dispatch for auth endpoints to prevent infinite loops
      if (!url.includes('/auth/refresh') &&
          !url.includes('/auth/login') &&
          !url.includes('/auth/me') &&
          !url.includes('/auth/logout')) {

        // Strip Authorization header on retry so the interceptor picks up
        // the fresh token from jwtHolder instead of reusing the stale one
        dispatch401Debounced(() => {
          const [retryInput, retryInit] = args;
          const retryHeaders = new Headers(retryInit?.headers || {});
          retryHeaders.delete('Authorization');
          return window.fetch(retryInput, { ...retryInit, headers: retryHeaders });
        });
      }
    }

    return response;
  };

  console.log('[Auth] Fetch interceptor enabled - auto-adding Authorization headers');
}

export function restoreFetch() {
  window.fetch = originalFetch;
}
